/* global Janus */

import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { connectWebSocket, subscribeToSDPStatus, sendCommand } from "../websocketService";
import Janus from "janus-gateway";
import adapter from 'webrtc-adapter'; // For WebRTC browser compatibility
import { setJanusInstance, getJanusInstance, setAudioPluginHandle, getAudioPluginHandle, addRemoteMediaStream, setLocalMediaStream } from '../WebRTCManager'; // Import WebRTCManager functions

window.adapter = adapter; // Globalize adapter if Janus expects it

const ConnectingPage = () => {
    const navigate = useNavigate();
    const location = useLocation(); // To access state passed from HomePage
    const [status, setStatus] = useState("Initializing connection...");
    const [error, setError] = useState(null);

    const sdpSubscriptionRef = useRef(null);

    const hasHandledRemoteJsep = useRef(false);
    
    const trickleCandidates = useRef([]); // To store ICE candidates before sending them

      //Store metadata to use during connection setup
        /*const customSessionId = useRef(null);
        const userName = useRef(null);
        const rtcSessionId = useRef(null);
        const rtcHandleId = useRef(null);*/

       

       const { customSessionId } = useParams();
       const userName = location.state?.userName || 'Guest'; // Default to 'Guest' if not provided   
     
       const janusInitialized = useRef(false); 
       const audioPluginHandleRef = useRef(null); // To store the audio plugin handle
           
       console.log("ConnectingPage: customSessionId from params:", customSessionId);
       useEffect(() => {

        console.log("ConnectingPage useEffect triggered with janus session ID:", janusInitialized.current); 
        if (janusInitialized.current) { 
            console.log("Janus already initialized for this component instance. Skipping re-initialization.");
            return; // Exit if already initialized
        }
        janusInitialized.current = true;

        hasHandledRemoteJsep.current = false;

        let janusInstance = getJanusInstance();
        let audioPluginHandle = getAudioPluginHandle();

        if (!customSessionId) {
            setError("Missing session details. Please start or join a room from the Home page.");
            //setTimeout(() => navigate('/'), 3000);
            return;
        }        
    
        Janus.init({
            debug: "all",
            callback: () => {
                // Mark Janus as initialized as soon as the init callback fires
                // This prevents subsequent calls to Janus.init from happening
                // if the component somehow re-renders before Janus object is fully set up.                
                if (!getJanusInstance()) {
                    console.log("Initializing new Janus instance for ConnectingPage...", getJanusInstance());
                    const newJanusInstance = new Janus({
                        server: "https://harmoniq-app.com/janus", //"http://localhost:8088/janus",
                        // Add STUN/TURN servers here
                        iceServers: [
                            { urls: "stun:stun.l.google.com:19302" },
                            // { urls: "turn:your-turn-server.com:3478", username: "your_username", credential: "your_password" } // Optional, for more complex network scenarios
                        ],
                        success: () => {  
                            console.log("Janus initialized successfully:", newJanusInstance);
                            setJanusInstance(newJanusInstance);                             
                            janusInstance = newJanusInstance; // Update the global Janus instance                          
                            setStatus("Janus connected. Attaching to plugin...");                            
                            console.log("Janus initialize: ", janusInitialized.current);
                            // This part is crucial: we are attaching to the existing session
                            // and then proceeding to RoomPage for media handling.
                            
                            const janusSessionId = janusInstance.getSessionId();
                            console.log("Janus initialized with session ID:", janusSessionId);
                            janusInstance.attach({
                                plugin: "janus.plugin.audiobridge",
                                opaqueId: "audioconference-fe-" + Janus.randomString(12),                                
                                success: (pluginHandle) => {
                                    setAudioPluginHandle(pluginHandle); // Store the plugin handle globally
                                    audioPluginHandleRef.current = pluginHandle;
                                    console.log("Plugin attached! Handle ID:", pluginHandle);
                                    setStatus("Plugin attached. Joining room...");
                                    
                                    // Start local audio capture immediately if not already done
                                  pluginHandle.createOffer({
                                    /*media: {
                                        audioSend: true,  // <-- CRUCIAL: Request to send audio
                                        audioRecv: true,  // <-- CRUCIAL: Request to receive audio (which you seem to be getting)
                                        videoSend: false,
                                        videoRecv: false
                                    },*/
                                    tracks: [                                        
                                        { type: 'audio', capture: true, recv: true, add: true }, // Requesting to capture and receive audio
                                        // For video:
                                        // { type: 'video', capture: true, recv: true, add: true },
                                        // If you don't want video, omit the video track or set capture: false
                                    ],
                                    trickle: true, // Disable trickle ICE for simplicity
                                    success: (jsep) => {
                                        if(jsep) {
                                        console.log("Got SDP offer for local stream:", jsep);
                                        /*const body = { request: "configure", muted: false };
                                        pluginHandle.send({ message: body, jsep: jsep });*/
                                        
                                        // Send SDP offer to your backend/Janus through WebSocket
                                        sendCommand('/session/sdp', {
                                            customSessionId: customSessionId,
                                            userId: userName,
                                            jsep: jsep,
                                            streamSessionId: janusSessionId, 
                                            streamHandleId: pluginHandle.id 
                                        });
                                        } else {
                                            console.error("No SDP offer received from Janus.");
                                        }

                                    },
                                    error: (error) => {
                                        console.error("WebRTC error (createOffer):", error);
                                    }
                                });
                                },
                                error: (error) => {
                                    console.error("  -- Error attaching plugin...", error);
                                    setError("Failed to attach audio plugin: " + error);
                                    janusInitialized.current = false;
                                },
                                onicecandidate: (candidate) => {
                                    // This callback is invoked when the browser's RTCPeerConnection generates an ICE candidate.
                                    if (candidate && candidate.candidate) {
                                        // Queue the candidate instead of sending immediately to Janus.
                                        console.log("Queuing ICE candidate:", candidate.candidate);
                                        //trickleCandidates.current.push(candidate);
                                        audioPluginHandle.send({
                                            message: { request: "trickle"},
                                            jsep: candidate // Send the candidate directly to Janus
                                        }); 
                                    } else {
                                        // A null candidate indicates that ICE gathering is complete.
                                        console.log("ICE gathering complete (no more candidates expected).");
                                        // We don't send candidates here, they will be sent when the SDP answer arrives.
                                    }
                                },
                                onlocaltrack: (track, on) => {
                                    // This callback replaces onlocalstream for modern Janus/WebRTC
                                    Janus.debug(" ::: Got a local track :::", track, on);
                                    console.log("onlocaltrack triggered:", track, on);
                
                                    if (track.kind === 'audio' && on) {
                                        // Create a MediaStream from the local audio track
                                        const localStream = new MediaStream([track]);
                                        setLocalMediaStream(localStream); // Store local stream in WebRTCManager
                                        setStatus("Local audio stream active.");
                                        // You might want to attach this stream to a local audio element here for testing
                                        // For a visualizer, RoomPage will pick it up from WebRTCManager
                                    } else if (track.kind === 'audio' && !on) {
                                        // Handle local audio track being removed/stopped (e.g., user mutes microphone)
                                        Janus.debug("Local audio track removed/stopped.");
                                        // You might want to clear the local stream from WebRTCManager here
                                        // setLocalMediaStream(null); // Or handle according to your app's logic
                                    }
                                },
                                onremotetrack: (track, mid, on) => {
                                    Janus.debug("Remote track (audio) received:", track, mid, on);
                                    console.log("Remote track (audio) received:", track, mid, on);
                                    if (track.kind === 'audio' && on) {
                                        const remoteStream = new MediaStream([track]);
                                        const remoteAudio = new Audio(); // A new audio element is created here
                                        remoteAudio.autoplay = true;
                                        remoteAudio.srcObject = remoteStream;
                                        addRemoteMediaStream(mid, remoteStream);
                                        setStatus("Remote audio stream active.");
                                        navigate(`/room/${customSessionId}`, {
                                            state: {  
                                                customSessionId: customSessionId,
                                                userName: userName
                                            }
                                        });
                                    }
                                },
                                // RoomPage will handle that.
                                onmessage: (msg, jsep) => {
                                    const audioPluginHandle = getAudioPluginHandle();
                                    // Handle messages from the plugin, e.g., participant updates
                                    console.log("ConnectingPage Janus message:", msg);                               

                                    
                                    if (jsep) {                                        
                                        console.log("Got SDP offer for remote media:", jsep);
                                        console.log("JSEP type:", jsep.type);
                                        console.log("answer true :", jsep.type === "answer");
                                        console.log("audio:", audioPluginHandleRef.current);
                                        // Handle SDP updates if needed, e.g., remote answers
                                        if (jsep.type==="answer" && !hasHandledRemoteJsep.current 
                                            && audioPluginHandleRef.current
                                        ) {
                                            console.log("Handling SDP answer from backend/Janus...", jsep);
                                            Janus.debug("Handling SDP answer from backend/Janus...", jsep);
                                            audioPluginHandleRef.current.handleRemoteJsep({jsep: jsep});
                                            hasHandledRemoteJsep.current = true;
                                            /*.then(() => {
                                                while (trickleCandidates.current.length > 0) {
                                                    const candidate = trickleCandidates.current.shift();
                                                    audioPluginHandleRef.current.trickle(candidate);
                                                }
                                                
                                                setStatus("Connected to room, establishing audio stream...");
                                            }).catch((error) => {
                                                Janus.error("WebRTC error: " + error.message);
                                                setError("WebRTC error: " + error.message);
                                                setStatus("Error: WebRTC connection failed.");
                                            });*/

                                            

                                            // IMPORTANT: Only send queued ICE candidates AFTER the remote SDP (answer) is set
                                            // This prevents the "No stream, queueing this trickle..." warnings
                                            /*if (trickleCandidates.current.length > 0) {
                                                console.log("Sending queued ICE candidates...");
                                                // Iterate and send candidates individually or as a batch.
                                                // Janus.js's 'send' with jsep: { trickle: [...] } allows batching
                                                getAudioPluginHandle().send({
                                                    message: { request: "trickle" },
                                                    jsep: {
                                                        sdp: null, // No SDP here, only candidates
                                                        trickle: trickleCandidates.current.map(c => ({
                                                            component: c.component,
                                                            sdpMid: c.sdpMid,
                                                            candidate: c.candidate
                                                        }))
                                                    }
                                                });
                                                trickleCandidates.current = []; // Clear the queue after sending
                                            }*/                                           
                                            }
                                    }
                                    
                                },                            
                                onremotestream: (stream) => {
                                  // This should ideally not be here.
                                  // This page's role is just to connect and pass to RoomPage.
                                  // The actual stream handling happens in RoomPage.
                                  console.log("ConnectingPage received remote stream (should not happen):", stream);
                                },
                                onlocalstream: (stream) => {
                                    console.log("Got local stream! WebRTC connection is likely established.", stream);
                                    // At this point, the local media stream (microphone) is ready
                                    // and the PeerConnection should be established for sending.
                                    setStatus("Local stream ready. Joining room and navigating...");

                                    // Now navigate to RoomPage.
                                    // This is a much more reliable point to navigate
                                    // as it confirms that the media pipeline is forming.
                                    setLocalMediaStream(stream);
                                    navigate(`/room/${customSessionId}`, {
                                        state: {
                                            customSessionId: customSessionId,
                                            userName: userName,
                                        }
                                    });
                                },
                            });
                        },
                        error: (error) => {
                            console.error("  -- Error initializing Janus...", error);
                            setError("Failed to initialize Janus: " + error);
                            janusInitialized.current = false;
                        },
                        destroyed: () => {
                            console.log("Janus instance destroyed for ConnectingPage.");
                            janusInitialized.current = false; 
                        }
                    })
                
                }
            }
        });

        return () => {
            if (sdpSubscriptionRef.current) {
                sdpSubscriptionRef.current.unsubscribe();
            }
            // Cleanup: Destroy the Janus instance and clear singleton only if *this* useEffect
            // created it, to avoid destroying instances used by other components or states.
            if (janusInstance) {
                console.log("Frontend: Janus was initialized, attempting to destroy (fallback cleanup).");
                /*janusInstance.destroy();
                janusInitialized.current = false; // Ensure flag is reset on unmount
                setJanusInstance(null);
                setAudioPluginHandle(null);
                audioPluginHandleRef.current = null;*/
                
            }
        };
    }, [customSessionId, navigate, userName]);

    useEffect(() => {
        /*
        // Subscribe only once
                    if (!sdpSubscriptionRef.current) {
                        sdpSubscriptionRef.current = subscribeToSDPStatus((response) => {
                            console.log("SDP Status Update:", response);
                            if (response.status && response.data && response.data.jsep) {
                                const jsep = response.data.jsep;
                                // Ensure we have an audio plugin handle and haven't handled this JSEP yet
                                if (audioPluginHandleRef.current && !hasHandledRemoteJsep.current) {
                                    Janus.debug("Handling SDP answer from backend/Janus (from websocket)...", jsep);
                                    audioPluginHandleRef.current.setRemoteDescription(jsep).then(() => {
                                        while (trickleCandidates.current.length > 0) {
                                            const candidate = trickleCandidates.current.shift();
                                            audioPluginHandleRef.current.trickle(candidate);
                                        }
                                        hasHandledRemoteJsep.current = true;
                                        setStatus("WebRTC connection established.");
                                    }).catch((error) => {
                                        Janus.error("WebRTC error: " + error.message);
                                        setError("WebRTC error during SDP answer processing: " + error.message);
                                        setStatus("Error: WebRTC connection failed.");
                                    });
                                }
                            }
    */
        sdpSubscriptionRef.current = subscribeToSDPStatus((sdpStatus) => {
                  console.log("SDP Status Update:", sdpStatus);
                  // Handle SDP updates if needed, e.g., remote answers
                  if (sdpStatus.status) {                                          
                        /*setStatus("Connection established! Navigating to room...");

                        console.log("Navigating to room with customSessionId:", customSessionId);
                      // Now that we've initiated joining the room, navigate to RoomPage
                      // RoomPage will then manage the streams
                      navigate(`/room/${customSessionId}`, {
                            state: {
                            customSessionId: customSessionId,
                            userName: userName
                            // Pass the Janus instance and plugin handle
                            // This is generally not recommended to pass complex objects via state
                            // Instead, RoomPage should re-attach or use the IDs
                            // For simplicity here, we'll assume RoomPage can re-initialize Janus and attach to the existing session/room using the IDs.
                            }
                        });*/
                  }
                  
              });

              return () => {
                if (sdpSubscriptionRef.current) {
                    sdpSubscriptionRef.current.unsubscribe(); // Unsubscribe
                    sdpSubscriptionRef.current = null;
                }
            };
        }, []);
         
              

    const handleGoHome = () => {
        navigate('/');
    };

    if (error) {
        return (
            <div className="min-h-screen bg-gray-900 text-white flex flex-col items-center justify-center p-4">
                <h2 className="text-2xl font-bold text-red-500">Connection Error</h2>
                <p className="mt-4 text-lg text-gray-400">{error}</p>
                <button
                    onClick={handleGoHome}
                    className="mt-6 px-6 py-3 bg-blue-600 rounded-lg text-lg font-bold hover:bg-blue-700 transition-colors duration-200"
                >
                    Go to Home
                </button>
            </div>
        );
    }

    return (
        <div className="min-h-screen text-white flex flex-col items-center justify-center p-4">
            <h2 className="text-2xl font-bold">Connecting to Room {customSessionId}...</h2>
            <p className="mt-4 text-lg text-gray-400">{status}</p>
            <div className="mt-8">
                <div className="loader ease-linear rounded-full border-4 border-t-4 border-gray-200 h-12 w-12 mb-4"></div>
                <style jsx>{`
                    .loader {
                        border-top-color: #3498db;
                        -webkit-animation: spinner 1.5s linear infinite;
                        animation: spinner 1.5s linear infinite;
                    }
                    @-webkit-keyframes spinner {
                        0% { -webkit-transform: rotate(0deg); }
                        100% { -webkit-transform: rotate(360deg); }
                    }
                    @keyframes spinner {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}</style>
            </div>
        </div>
    );
};

export default ConnectingPage;